'use client';

import { Form } from '@statura/ui';
import { PageTitle } from '@statura/ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { LogOut, RefreshCw } from 'lucide-react';
import { Card, CardContent, buttonVariants } from '@statura/ui';

/**
 * Honest-transient degradation for an ORG-RESOLUTION FAILURE (P1 2026-08-19).
 *
 * Rendered by `(dashboard)/layout.tsx` when `resolveUserContextDetailed`
 * returns `indeterminate` — the users/tenant lookup errored, so we do NOT know
 * whether the account is linked to an organisation. The permanent "Account not
 * linked to an organisation" copy (NoOrganisationState) is reserved for a
 * POSITIVE service-role lookup that found no membership; showing it on a
 * transient failure told legitimate tenant members their account was broken
 * and left them with no working exit.
 *
 * Behaviour: auto-retries the server render a few times with backoff (the
 * observed outages recover within seconds), offers a manual Try-again button,
 * and offers sign-out (native form POST to /auth/signout, which terminates the
 * session even when resolution is still failing).
 */

const AUTO_RETRY_DELAYS_MS = [2000, 4000, 8000];

export function OrgResolutionRetryState() {
  const router = useRouter();
  const [attempt, setAttempt] = useState(0);
  const [retrying, setRetrying] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const retry = useCallback(() => {
    setRetrying(true);
    // router.refresh() re-runs the server layout; if resolution succeeds the
    // real workspace replaces this screen. The pending flag only drives the
    // button label — refresh() resolves via a new server render, not a promise
    // we can await here.
    router.refresh();
    setTimeout(() => {
      if (mounted.current) setRetrying(false);
    }, 1500);
  }, [router]);

  useEffect(() => {
    if (attempt >= AUTO_RETRY_DELAYS_MS.length) return;
    const timer = setTimeout(() => {
      setAttempt((current) => current + 1);
      retry();
    }, AUTO_RETRY_DELAYS_MS[attempt]);
    return () => clearTimeout(timer);
  }, [attempt, retry]);

  const autoRetryExhausted = attempt >= AUTO_RETRY_DELAYS_MS.length;

  return (
    <div
      className="flex min-h-[60vh] items-center justify-center px-4 py-10"
      data-testid="org-resolution-retry-state"
    >
      <Card className="w-full max-w-xl">
        <CardContent className="space-y-6 p-6 sm:p-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-border bg-surface-soft">
              <RefreshCw
                className={`h-6 w-6 text-text-muted ${autoRetryExhausted ? '' : 'animate-spin'}`}
                aria-hidden="true"
              />
            </div>
            <div className="min-w-0">
              <PageTitle className="font-heading text-section-title text-text-heading">
                We couldn&apos;t load your organisation just now
              </PageTitle>
              <p
                className="mt-2 max-w-md font-body text-body leading-6 text-text-muted"
                role="status"
              >
                {autoRetryExhausted
                  ? 'This looks like a temporary problem on our side — your account is fine. Use Try again in a moment, or sign out and sign back in.'
                  : 'This is usually momentary — retrying automatically…'}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={retry}
              disabled={retrying}
              className={buttonVariants({ className: 'justify-center' })}
            >
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              {retrying ? 'Retrying…' : 'Try again'}
            </button>
            <Form action="/auth/signout" method="post">
              <button
                type="submit"
                className={buttonVariants({
                  variant: 'secondary',
                  className: 'justify-center',
                })}
              >
                <LogOut className="h-4 w-4" aria-hidden="true" />
                Sign out
              </button>
            </Form>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
